import { Camera } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

const WILAYAS = ["01 - Adrar","02 - Chlef","03 - Laghouat","04 - Oum El Bouaghi","05 - Batna","06 - Béjaïa","07 - Biskra","08 - Béchar","09 - Blida","10 - Bouira","11 - Tamanrasset","12 - Tébessa","13 - Tlemcen","14 - Tiaret","15 - Tizi Ouzou","16 - Alger","17 - Djelfa","18 - Jijel","19 - Sétif","20 - Saïda","21 - Skikda","22 - Sidi Bel Abbès","23 - Annaba","24 - Guelma","25 - Constantine","26 - Médéa","27 - Mostaganem","28 - M'Sila","29 - Mascara","30 - Ouargla","31 - Oran","32 - El Bayadh","33 - Illizi","34 - Bordj Bou Arréridj","35 - Boumerdès","36 - El Tarf","37 - Tindouf","38 - Tissemsilt","39 - El Oued","40 - Khenchela","41 - Souk Ahras","42 - Tipaza","43 - Mila","44 - Aïn Defla","45 - Naâma","46 - Aïn Témouchent","47 - Ghardaïa","48 - Relizane","49 - Timimoun","50 - Bordj Badji Mokhtar","51 - Ouled Djellal","52 - Béni Abbès","53 - In Salah","54 - In Guezzam","55 - Touggourt","56 - Djanet","57 - El M'Ghair","58 - El Meniaa","59 - Aflou","60 - Barika","61 - Ksar Chellala","62 - Messaâd","63 - Aïn Oussera","64 - Bou Saâda","65 - El Abiodh Sidi Cheikh","66 - El Kantara","67 - Bir El Ater","68 - Ksar El Boukhari","69 - El Aricha"];

const PANNE_TYPES = {
  fauteuil_dentaire:     "Fauteuil dentaire",
  radiologie:            "Radiologie (RVG, Panoramique)",
  turbine_contre_angle:  "Turbines & Contre-angles",
  sterilisation:         "Stérilisation & Autoclave",
  compresseur:           "Compresseur",
  aspiration:            "Aspiration",
  unite_dentaire:        "Unité dentaire complète",
  scanner_cao_cam:       "Scanner & CAO/CAM",
  electronique:          "Électronique générale",
  autre:                 "Autre",
};

const NouvelAppelOffre = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    title: "", description: "", type_panne: "autre",
    marque_appareil: "", modele_appareil: "",
    urgence: false, wilaya: "", city: "",
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const uploadImage = async (file) => {
    const token = localStorage.getItem("dz_token");
    const data = new FormData();
    data.append("file", file);
    const r = await fetch(API + "/api/upload", {
      method: "POST",
      headers: { "Authorization": "Bearer " + token },
      body: data,
    });
    if (r.ok) {
      const d = await r.json();
      return d.url;
    }
    return null;
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const urls = [];
    for (const file of files.slice(0, 4)) {
      const url = await uploadImage(file);
      if (url) urls.push(url);
    }
    setImages(prev => [...prev, ...urls]);
    setUploading(false);
    toast({ title: `${urls.length} image(s) uploadée(s)` });
  };

  const submit = async () => {
    if (!form.title || !form.wilaya) {
      toast({ title: "Erreur", description: "Titre et wilaya obligatoires", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/appels-offres", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ ...form, images }),
      });
      const d = await r.json();
      if (r.ok) {
        toast({ title: "✅ Appel d'offre lancé !", description: "Les réparateurs de votre wilaya ont été notifiés !" });
        navigate("/reparateurs");
      } else {
        toast({ title: "Erreur", description: d.detail || "Erreur", variant: "destructive" });
      }
    } catch {}
    setSaving(false);
  };

  if (!user) {
    navigate("/connexion");
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/reparateurs")} className="flex items-center gap-2 text-[#1F4D3F] text-sm mb-6 hover:underline">
        ← Retour aux réparateurs
      </button>
      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        <div className="bg-[#C75B27] px-6 py-5">
          <h1 className="text-white font-serif text-2xl font-semibold">Lancer un appel d'offre</h1>
          <p className="text-white/80 text-sm mt-1">Les techniciens de votre wilaya seront notifiés automatiquement</p>
        </div>
        <div className="p-6 space-y-4">
          {/* Titre */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-1">Titre de la panne *</label>
            <input value={form.title} onChange={e => set("title", e.target.value)}
              placeholder="ex: Fauteuil dentaire en panne — moteur ne démarre plus"
              className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C75B27]" />
          </div>

          {/* Type de panne */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-2">Type d'équipement *</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PANNE_TYPES).map(([k, v]) => (
                <button key={k} onClick={() => set("type_panne", k)}
                  className={"px-3 py-2 rounded-xl border text-xs font-medium text-left transition-all " +
                    (form.type_panne === k ? "bg-[#C75B27] text-white border-[#C75B27]" : "bg-white border-[#E8E0D0] text-[#5A5247]")}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Marque + Modèle */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">Marque</label>
              <input value={form.marque_appareil} onChange={e => set("marque_appareil", e.target.value)}
                placeholder="ex: Stern Weber, NSK..."
                className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">Modèle</label>
              <input value={form.modele_appareil} onChange={e => set("modele_appareil", e.target.value)}
                placeholder="ex: S200, Pana-Air..."
                className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-1">Description de la panne</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)}
              rows={3} placeholder="Décrivez le problème en détail..."
              className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none resize-none" />
          </div>

          {/* Wilaya + Ville */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">Wilaya *</label>
              <select value={form.wilaya} onChange={e => set("wilaya", e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm bg-white focus:outline-none">
                <option value="">Sélectionner...</option>
                {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">Ville</label>
              <input value={form.city} onChange={e => set("city", e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" />
            </div>
          </div>

          {/* Urgence */}
          <div className="flex items-center gap-3 p-3 bg-[#FFF3E0] rounded-xl">
            <input type="checkbox" id="urgence" checked={form.urgence}
              onChange={e => set("urgence", e.target.checked)}
              className="w-4 h-4 accent-[#C75B27]" />
            <label htmlFor="urgence" className="text-sm font-medium text-[#C75B27]">
              ⚡ Urgence — intervention requise dans les 24h
            </label>
          </div>

          {/* Upload images */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-2">
              Photos de la panne (optionnel — max 4)
            </label>
            <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-[#E8E0D0] rounded-xl p-4 hover:border-[#C75B27] transition-colors">
              <Camera className="w-6 h-6 text-[#7A6F5C]" />
              <div>
                <div className="text-sm font-medium text-[#2D2A26]">
                  {uploading ? "Upload en cours..." : "Cliquer pour ajouter des photos"}
                </div>
                <div className="text-xs text-[#9A8F82]">JPG, PNG — max 5MB par image</div>
              </div>
              <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" disabled={uploading} />
            </label>
            {images.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {images.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url.startsWith('/api') ? API + url : url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#E8E0D0]" />
                    <button onClick={() => setImages(imgs => imgs.filter((_, j) => j !== i))}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={submit} disabled={saving || uploading}
            className="w-full bg-[#C75B27] hover:bg-[#a84d21] text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-colors">
            {saving ? "Envoi en cours..." : "🔔 Lancer l'appel d'offre"}
          </button>
          <p className="text-xs text-center text-[#9A8F82]">
            Tous les techniciens agréés de votre wilaya seront notifiés par WhatsApp
          </p>
        </div>
      </div>
    </div>
  );
};

export default NouvelAppelOffre;
